<template>
  <div class="shape-format">
    <h2 class="shape-format-title">容器属性</h2>
    <label-button
      @click="toggleEdit"
      :text="currentEffect.children.length ? '更换素材' : '添加素材'"
      >内容素材</label-button
    >
    <label-button
      @click="selectContent"
      v-if="currentEffect.children.length"
      text="素材调整"
      >素材设置</label-button
    >
    <div class="border-bottom"></div>
    <h2 class="shape-format-title shape-format-title--offset">容器设置</h2>
    <LabelSlider
      label="透明度"
      class="text-shadow-opacity"
      :value="effectInfo.opacity"
      type="opacity"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      v-model="currentEffect.content.rotate"
      :precision="0"
    />
    <flip-editor v-model="currentEffect.content.flip" v-if="false" />
    <effect-param-editor
      v-for="desc in shapeDescs"
      :tag="desc.tag"
      :attrs="desc.attrs"
      v-model="currentEffect.content[desc.prop]"
      :key="desc.prop"
    />
    <position-editor :effect="currentEffect" />
    <div class="border-bottom"></div>
    <h2 class="shape-format-title shape-format-title--offset">转场效果</h2>
    <LabelInput label="延迟出现（秒）" v-model="displayStart" :time="true" />
    <LabelInput label="显示时长（秒）" v-model="displayDuration" :time="true" />

    <LabelOption
      label="入场效果"
      :optionList="enterEffectList"
      v-model="currentEffect.enter"
    />
    <LabelInput
      label="入场效果显示时长（秒）"
      :time="true"
      v-model="currentEffect.enterDuration"
      :disabled="currentEffect.enter === 'none'"
    />
    <LabelOption
      label="出场效果"
      :optionList="leaveEffectList"
      v-model="currentEffect.leave"
    />
    <LabelInput
      label="出场效果显示时长（秒）"
      :time="true"
      v-model="currentEffect.leaveDuration"
      :disabled="currentEffect.leave === 'none'"
    />
    <div class="border-bottom"></div>

    <h2 class="shape-format-title" style="margin-top: 12px">动态效果</h2>
    <LabelSwitch :fn="fn" label="动态特效" v-model="currentEffect.showTracks" />
    <SingleEffectEditor
      label="特效"
      v-for="(track, i) in currentEffect.tracks"
      :disabled="!currentEffect.showTracks"
      :key="i"
      :track="track"
      :effectList="effectListAt(i)"
      @delete="removeTrack(i)"
      :deletable="currentEffect.tracks.length > 1"
    />
    <a-button
      v-if="currentEffect.tracks.length < 5"
      class="insert-track"
      @click="insertTrack"
      block
      :disabled="!currentEffect.showTracks"
      icon="plus"
      >继续添加</a-button
    >
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import LabelInput from "../base/LabelInput";
import LabelSlider from "../base/LabelSlider";
import EffectParamEditor from '../base/EffectParamEditor';
import LabelButton from '../base/LabelButton';
import LabelSwitch from '../base/LabelSwitch';
import LabelOption from '../base/LabelOption';
import SingleEffectEditor from '../base/SingleEffectEditor';
import PositionEditor from '../base/PositionEditor';
import FlipEditor from '../base/FlipEditor';
import { effectParams } from '../../utils/textEffectMap';
import { shapeParamDescs } from '../../utils/shapes';
import { getCropDuration } from '../../utils/util';
import { clamp } from '../../utils';

export default {
  name: "TextFormat",
  components: {
    LabelInput,
    LabelSlider,
    EffectParamEditor,
    LabelButton,
    LabelSwitch,
    LabelOption,
    SingleEffectEditor,
    PositionEditor,
    FlipEditor
  },
  data() {
    return {
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ],
      form: this.$form.createForm(this, { name: 'test' })
    };
  },
  computed: {
    ...mapState(['editingContainer', 'effectNames', 'generalEffects', 'effects']),
    ...mapGetters(['effectInfo', 'currentEffect', 'activeFragment']),
    fontList() {
      const { fontList } = this.$store.state;
      return fontList.map(item => ({
        name: item.font_name,
        value: item.font_name
      }));
    },
    shapeDescs() {
      return shapeParamDescs[this.currentEffect.content.shape] || [];
    },
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, 'start', Math.max(value, 0));
      }
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.setDuration(value);
      }
    }
  },
  methods: {
    ...mapMutations(['handleCurrentIcon', 'handleCurrentMenu', 'handleContainerEdit']),
    fn(showTracks) {
      if (showTracks) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    setDuration(value) {
      const children = this.currentEffect.children;
      if (children) {
        const max = Math.min.apply(Math, children.map(c => {
          const child = this.effects[c];
          if (child && child.type === 'video') {
            return getCropDuration(child.content);
          }
          return Infinity;
        }));

        this.$set(this.currentEffect, 'duration', clamp(value, 400, max));
      } else {
        this.$set(this.currentEffect, 'duration', Math.max(value, 400));
      }
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    toggleEdit() {
      // TODO:
      this.handleContainerEdit(!this.editingContainer && this.currentEffect.id || null);
      this.handleCurrentIcon('material');
      this.handleCurrentMenu('MaterialMenu');
    },
    selectContent() {
      this.$store.commit('handleEffectSelect', [this.currentEffect.children[0]]);
    }
  },
  beforeDestroy() {
    this.handleContainerEdit(null);
  }
}
</script>

<style scoped>
.shape-format {
  background: rgba(248, 248, 248, 1);
  padding: 13px;
  box-sizing: border-box;
}

.shape-format-title {
  font-size: 14px;
  font-weight: 500;
  /* color: #000; */
  margin: 0 0 10px;
}

.shape-format-title--offset {
  margin-top: 12px;
}

.setting {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.shape-format .border-bottom {
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(216, 216, 216, 1);
  width: 100%;
}

.insert-track {
  border-color: #e5e5e7;
  background: #fff;
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.75);
  border-radius: 2px;
}

.insert-track:hover,
.insert-track:focus {
  border-color: #ccc;
}

.insert-track:disabled {
  border-color: #dcdcdc;
  background: #fff;
  color: rgba(0, 0, 0, 0.75);
  opacity: 0.35;
}
</style>

<style>
.shape-format .setting-input {
  margin-left: 3px;
}

.shape-format .ant-radio-group {
  margin-left: 3px;
}
</style>
